<template>
    <div>
        <CompassAlert :type="CompassEnums.AlertType.INFORMATIVE" :description="hint" />
        <div>
            <CompassTable
                :size="CompassEnums.TableSize.MEDIUM"
                :hasExpandableRows="props.isStockEnabled"
            >
                <CompassTableHead>
                    <CompassTableHeadCell>
                        {{ $t.__('ocr.OcrVerificationProductsTable.supplier_product') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        v-if="props.isLineItemsEnabled"
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('common.labels.quantity_shorten') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('common.labels.cost_excluding_tax') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ $t.__('ocr.OcrVerificationForm.tax_rate') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="props.isLineItemsEnabled">
                        {{ $t.__('ocr.OcrVerificationProductsTable.unit_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('ocr.OcrVerificationProductsTable.tax_amount') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell
                        class="ocr-verification-products-table__header--align-right"
                    >
                        {{ $t.__('ocr.OcrVerificationProductsTable.amount_incl_tax') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="props.isLineItemsEnabled">
                        {{ $t.__('ocr.OcrVerificationProductsTable.catalogue_link_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell>
                        {{ $t.__('common.labels.accounting_code') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell v-if="showAnalyticCodeColumn">
                        {{ $t.__('ocr.OcrVerificationProductsTable.analytic_code_optional') }}
                    </CompassTableHeadCell>
                    <CompassTableHeadCell id="delete" fixed="right" :defaultWidth="80" />
                </CompassTableHead>
                <CompassTableBody>
                    <OcrVerificationProductsTableRow
                        v-for="(product, index) in products"
                        :key="index"
                        :disabled="props.disabled"
                        :isElectronic="props.isElectronic"
                        :product="product"
                        :index="index"
                        :canExpand="props.isStockEnabled && hasCatalogueLink(product)"
                        :isExpanded="expandedIndex === index"
                        :canDelete="canDelete"
                        :lineError="lineErrors[index]"
                        :taxRates="props.taxRates"
                        :accountingCodes="props.accountingCodes"
                        :units="props.units"
                        :warehouses="props.warehouses"
                        :showAnalyticCode="props.showAnalyticCode"
                        :isLineItemsEnabled="props.isLineItemsEnabled"
                        :currencySymbol="props.currencySymbol"
                        :formatDateDisplayed="props.formatDateDisplayed"
                        @description-update="handleDescriptionUpdate"
                        @quantity-update="handleQuantityUpdate"
                        @price-without-tax-update="handlePriceWithoutTaxUpdate"
                        @tax-rate-update="handleTaxRateUpdate"
                        @unit-update="handleUnitUpdate"
                        @catalogue-link-update="handleCatalogueLinkUpdate"
                        @accounting-code-update="handleAccountingCodeUpdate"
                        @analytic-code-update="handleAnalyticCodeUpdate"
                        @toggle-expand="handleToggleExpand"
                        @stock-out-date-update="handleStockOutDateUpdate"
                        @warehouse-update="handleWarehouseUpdate"
                        @serial-numbers-update="handleSerialNumbersUpdate"
                        @tax-amount-override="handleTaxAmountOverride"
                        @amount-including-tax-override="handleAmountIncludingTaxOverride"
                        @request-delete="handleRequestProductDelete"
                        @request-duplicate="handleRequestDuplicate"
                        @product-updated="handleProductUpdated"
                    />
                </CompassTableBody>
            </CompassTable>
            <CompassButton
                class="ocr-verification-products-table__add-line"
                :label="$t.__('ocr.OcrVerificationForm.add_line')"
                :type="CompassEnums.ButtonType.TERTIARY"
                :disabled="props.disabled || props.isElectronic"
                leftIcon="plus"
                @click="handleAddProduct"
            />
        </div>
    </div>
</template>

<script setup>
import { computed, inject, ref, toRefs, watch } from 'vue';
import Schema from 'async-validator';
import OcrVerificationProductsTableRow from 'vcomponents/ocr/OcrVerificationProductsTableRow.vue';
import Item from 'api/item';

// --- Props ---

const props = defineProps({
    products: {
        type: Array,
        required: true,
    },
    units: {
        type: Array,
        default: () => [],
    },
    warehouses: {
        type: Array,
        default: () => [],
    },
    isStockEnabled: {
        type: Boolean,
        default: false,
    },
    isLineItemsEnabled: {
        type: Boolean,
        default: false,
    },
    hint: {
        type: String,
        required: true,
    },
    rulesLine: {
        type: Object,
        required: true,
    },
    accountingCodes: {
        type: Array,
        required: true,
    },
    taxRates: {
        type: Array,
        required: true,
    },
    showAnalyticCode: {
        type: Boolean,
        default: false,
    },
    currencySymbol: {
        type: String,
        default: null,
    },
    formatDateDisplayed: {
        type: String,
        default: null,
    },
    addProduct: {
        type: Function,
        required: true,
    },
    requestProductDelete: {
        type: Function,
        required: true,
    },
    updateDescription: {
        type: Function,
        required: true,
    },
    setProductDescriptionLabel: {
        type: Function,
        required: true,
    },
    hideDefaultValues: {
        type: Function,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    isElectronic: {
        type: Boolean,
        default: false,
    },
});

const { products, rulesLine } = toRefs(props);
const $t = inject('$t');
const $pushToast = inject('$pushToast');

// --- Constants ---

const FIELD_KEYS = ['description', 'priceWithoutTax', 'taxRateId', 'accountingCode'];

// --- Helpers ---

const hasCatalogueLink = (product) => {
    const link = product?.catalogueLink;
    if (typeof link === 'string') {
        return link.trim().length > 0;
    }
    return !!link;
};

const parseSelectValue = (value) => {
    if (value === '' || value === null) {
        return null;
    }
    const numValue = Number(value);
    return Number.isNaN(numValue) ? value : numValue;
};

const emptyLineErrors = () => ({
    description: '',
    priceWithoutTax: '',
    taxRateId: '',
    accountingCode: '',
});

// --- Refs ---

const expandedIndex = ref(
    products.value.length > 0 && hasCatalogueLink(products.value[0]) ? 0 : null,
);
const lineErrors = ref([]);

// --- Computed ---

const canDelete = computed(() => products.value.length > 1);
const showAnalyticCodeColumn = computed(() => props.showAnalyticCode && props.isLineItemsEnabled);

const validators = computed(() => {
    const rules = rulesLine.value || {};
    const map = {};

    for (const field of FIELD_KEYS) {
        if (rules[field]?.length) {
            map[field] = new Schema({ [field]: rules[field] });
        }
    }

    return map;
});

// --- Watchers ---

const resetLineErrors = () => {
    lineErrors.value = products.value.map(() => emptyLineErrors());
};

watch(products, resetLineErrors, { immediate: true });

watch(
    () => products.value.length,
    (length, oldLength = 0) => {
        if (length > oldLength) {
            for (let i = oldLength; i < length; i += 1) {
                lineErrors.value[i] = emptyLineErrors();
            }
            if (oldLength === 0 && length > 0 && hasCatalogueLink(products.value[0])) {
                expandedIndex.value = 0;
            }
        } else {
            lineErrors.value = lineErrors.value.slice(0, length);
            if (expandedIndex.value !== null && expandedIndex.value >= length) {
                expandedIndex.value = null;
            }
        }
    },
);

// --- Validation ---

const setFieldError = (index, field, message) => {
    if (!lineErrors.value[index]) {
        lineErrors.value[index] = emptyLineErrors();
    }
    lineErrors.value[index][field] = message;
};

const validateField = async (index, field) => {
    const validator = validators.value[field];

    if (!validator || !products.value[index]) {
        setFieldError(index, field, '');
        return true;
    }

    try {
        await validator.validate({ [field]: products.value[index][field] }, { first: true });
        setFieldError(index, field, '');
        return true;
    } catch (error) {
        const message = error?.errors?.[0]?.message ?? '';
        setFieldError(index, field, message);
        return false;
    }
};

const validateLine = async (index) => {
    let valid = true;

    for (const field of FIELD_KEYS) {
        const fieldValid = await validateField(index, field);
        if (!fieldValid) {
            valid = false;
        }
    }

    return valid;
};

const validate = async () => {
    resetLineErrors();

    let valid = true;
    for (let i = 0; i < products.value.length; i += 1) {
        const lineValid = await validateLine(i);
        if (!lineValid) {
            valid = false;
        }
    }

    return valid;
};

defineExpose({ validate });

// --- Event handlers ---

const handleDescriptionUpdate = (value, product, index) => {
    product.description = value;
    props.updateDescription(value);
    validateField(index, 'description');
};

const handleQuantityUpdate = (value, product) => {
    product.quantity = value === '' ? null : value;
    props.hideDefaultValues();
};

const handlePriceWithoutTaxUpdate = (value, product, index) => {
    product.priceWithoutTax = value;
    props.hideDefaultValues();
    validateField(index, 'priceWithoutTax');
};

const handleTaxRateUpdate = (value, product, index) => {
    product.taxRateId = parseSelectValue(value);
    props.hideDefaultValues();
    validateField(index, 'taxRateId');
};

const handleUnitUpdate = (value, product) => {
    product.unit = parseSelectValue(value);
};

const handleCatalogueLinkUpdate = async (value, product) => {
    product.catalogueLink = value === '' ? null : value;

    if (product.catalogueLink && !product.catalogueLink.id && product.catalogueLink.value) {
        product.catalogueLink.id = product.catalogueLink.value;
    }

    const itemId = product.catalogueLink?.id;
    if (itemId) {
        try {
            const { data } = await Item.getItemById(itemId);
            if (data.accounting_purchase_code_id) {
                product.accountingCode = data.accounting_purchase_code_id;
            }
            product.catalogueLink.itemType = data.type === 'service' ? 'service' : 'item';
        } catch {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('ocr.OcrVerificationForm.error_generic'),
                timeout: 5000,
            });
        }
    }
};

const handleAccountingCodeUpdate = (value, product, index) => {
    product.accountingCode = value === '' ? null : value;
    props.setProductDescriptionLabel(product);
    validateField(index, 'description');
    validateField(index, 'accountingCode');
};

const handleAnalyticCodeUpdate = (value, product) => {
    product.analyticCode = value === '' ? null : value;
};

const handleToggleExpand = (product, index, expanded) => {
    if (!hasCatalogueLink(product)) {
        return;
    }

    const shouldExpand = typeof expanded === 'boolean' ? expanded : expandedIndex.value !== index;
    expandedIndex.value = shouldExpand ? index : null;
};

const handleStockOutDateUpdate = (value, product) => {
    product.stockOutDate = value === '' ? null : value;
};

const handleWarehouseUpdate = (value, product) => {
    product.warehouseId = parseSelectValue(value);
};

const handleSerialNumbersUpdate = (value, product) => {
    product.serialNumbers = value === '' ? null : value;
};

const handleTaxAmountOverride = (value, product) => {
    product.taxAmountOverride = value;
    props.hideDefaultValues();
};

const handleAmountIncludingTaxOverride = (value, product) => {
    product.amountIncludingTaxOverride = value;
    props.hideDefaultValues();
};

const handleAddProduct = () => {
    props.addProduct();
};

const handleRequestProductDelete = (index) => {
    props.requestProductDelete(index);
};

const handleRequestDuplicate = (index) => {
    props.addProduct(products.value[index]);
};

const handleProductUpdated = (itemId, updatedName, accountingPurchaseCodeId) => {
    for (const product of products.value) {
        if (product.catalogueLink?.id === itemId) {
            product.catalogueLink.label = updatedName;
            if (accountingPurchaseCodeId) {
                product.accountingCode = accountingPurchaseCodeId;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.ocr-verification-products-table__add-line {
    border: 0;
    padding-left: 0;
    margin-top: var(--spacing-2);
}

.ocr-verification-products-table__header--align-right {
    :deep(.compass-table__cell-tooltip) {
        justify-content: flex-end;
    }
}
</style>
